// Turning a raw screenshot into the thing people actually post.
//
// One function does the whole picture: the regions the visitor painted over,
// then the card, its corners, its shadow and the ground behind it. Everything
// is derived from the width it is asked to draw at, so the same code renders
// the small preview that has to keep up with a slider and the full size copy
// that gets saved, and the two cannot drift apart.
//
// Regions are stored as fractions of the image rather than pixels, for the same
// reason: a box painted on a 1200 pixel preview has to land in the same place
// on a 3840 pixel export.

import { sizeOf } from './images.js';

/** The grounds offered behind the screenshot. */
export const BACKDROPS = [
    // First, and what the page starts with: the screenshot as it came, only
    // redacted. Dressing it up is a choice, not something to undo.
    { id: 'off',    label: 'None, as it comes' },
    { id: 'ocean',  label: 'Ocean',  from: '#2563eb', to: '#4f46e5' },
    { id: 'sunset', label: 'Sunset', from: '#f97316', to: '#ec4899' },
    { id: 'mint',   label: 'Mint',   from: '#10b981', to: '#06b6d4' },
    { id: 'dusk',   label: 'Dusk',   from: '#334155', to: '#0f172a' },
    { id: 'blush',  label: 'Blush',  from: '#fda4af', to: '#fdba74' },
    { id: 'solid',  label: 'One colour' },
    { id: 'none',   label: 'Transparent' },
];

/** Shadow strength, as a share of the picture's long edge. */
const SHADOWS = {
    none:   { blur: 0,     offset: 0,     alpha: 0 },
    soft:   { blur: 0.035, offset: 0.012, alpha: 0.28 },
    strong: { blur: 0.06,  offset: 0.025, alpha: 0.42 },
};

const RATIOS = {
    auto: null,
    '16:9': 16 / 9,
    '4:3': 4 / 3,
    '1:1': 1,
};

/** The three dots on the window bar, in the order every desktop draws them. */
const DOTS = ['#ff5f57', '#febc2e', '#28c840'];

/**
 * A rounded rectangle path.
 *
 * Hand-drawn rather than ctx.roundRect, which Safari only learned recently and
 * which would take the corners off every screenshot on an older one without
 * anything in the interface admitting it.
 */
function roundedPath(ctx, x, y, width, height, radius) {
    const r = Math.max(0, Math.min(radius, width / 2, height / 2));
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.arcTo(x + width, y, x + width, y + r, r);
    ctx.lineTo(x + width, y + height - r);
    ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
    ctx.lineTo(x + r, y + height);
    ctx.arcTo(x, y + height, x, y + height - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
}

/** Whether this browser can blur while drawing. Safari before 17 cannot. */
let filterSupport = null;
function supportsFilter() {
    if (filterSupport === null) {
        const ctx = document.createElement('canvas').getContext('2d');
        filterSupport = typeof ctx.filter === 'string';
    }
    return filterSupport;
}

/**
 * The screenshot at `width` x `height`, with every region covered.
 *
 * The clean picture is kept on its own canvas and every region is read from
 * there, so two overlapping boxes both work from the original rather than one
 * of them smearing the other's blur back across the picture.
 */
function applyRegions(image, state, width, height) {
    const base = document.createElement('canvas');
    base.width = width;
    base.height = height;
    const baseCtx = base.getContext('2d');
    baseCtx.imageSmoothingEnabled = true;
    baseCtx.imageSmoothingQuality = 'high';
    baseCtx.drawImage(image, 0, 0, width, height);

    if (!state.regions.length) return base;

    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const ctx = out.getContext('2d');
    ctx.drawImage(base, 0, 0);

    for (const region of state.regions) {
        const x = Math.round(region.x * width);
        const y = Math.round(region.y * height);
        const w = Math.round(region.w * width);
        const h = Math.round(region.h * height);
        if (w < 1 || h < 1) continue;

        // A solid bar is the only one of the three that removes anything. Blur
        // and pixelation both leave a shadow of the original behind, which is
        // why the interface says so and why this is the default.
        const mode = state.redactMode === 'blur' && !supportsFilter() ? 'pixelate' : state.redactMode;

        if (mode === 'fill') {
            ctx.fillStyle = state.redactColour;
            ctx.fillRect(x, y, w, h);
            continue;
        }

        if (mode === 'pixelate') {
            // Scaled down to a handful of blocks and blown straight back up.
            const blocks = Math.max(3, Math.round(Math.min(w, h) / 14));
            const smallW = Math.max(1, Math.round(blocks * (w / Math.min(w, h))));
            const smallH = Math.max(1, Math.round(blocks * (h / Math.min(w, h))));

            const small = document.createElement('canvas');
            small.width = smallW;
            small.height = smallH;
            const smallCtx = small.getContext('2d');
            smallCtx.imageSmoothingEnabled = true;
            smallCtx.drawImage(base, x, y, w, h, 0, 0, smallW, smallH);

            ctx.save();
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(small, 0, 0, smallW, smallH, x, y, w, h);
            ctx.restore();
            continue;
        }

        // Blurred hard enough that the radius is a share of the box rather than
        // a fixed number of pixels, so a wide banner is not left legible.
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, w, h);
        ctx.clip();
        ctx.filter = `blur(${Math.max(4, Math.round(Math.min(w, h) / 6))}px)`;
        ctx.drawImage(base, 0, 0);
        ctx.restore();
    }

    return out;
}

/**
 * Where everything lands, without drawing any of it.
 *
 * Separate from the drawing because the interface wants the finished dimensions
 * on every slider move, to show what the export will be, and working that out
 * by composing a second full size copy would redraw a 4K screenshot for a line
 * of text.
 *
 * @param {object} state
 * @param {number} imageWidth  Width to render the screenshot itself at.
 */
/**
 * The state the layout should use. With the backdrop off, everything that only
 * makes sense around a backdrop is off too, so the result is the picture at its
 * own size, whatever the other controls still hold.
 */
function dressed(state) {
    if (state.backdrop !== 'off') return state;
    return { ...state, padding: 0, radius: 0, shadow: 'none', frame: 'none', ratio: 'auto' };
}

export function layout(rawState, imageWidth) {
    const state = dressed(rawState);
    const source = sizeOf(state.image);
    const iw = Math.max(1, Math.round(imageWidth));
    const ih = Math.max(1, Math.round((source.height / source.width) * iw));
    const long = Math.max(iw, ih);

    const pad = Math.round(state.padding * long);
    const bar = state.frame === 'bar' ? Math.max(16, Math.round(long * 0.03)) : 0;
    const radius = Math.round(state.radius * long);

    const cardW = iw;
    const cardH = ih + bar;

    let outerW = cardW + pad * 2;
    let outerH = cardH + pad * 2;

    // A fixed ratio only ever adds ground on the short side. Cropping to it
    // would cut the screenshot, which is the one thing nobody asking for a
    // square wants.
    const ratio = RATIOS[state.ratio];
    if (ratio) {
        if (outerW / outerH < ratio) {
            outerW = Math.round(outerH * ratio);
        } else {
            outerH = Math.round(outerW / ratio);
        }
    }

    return {
        iw, ih, long, bar, radius,
        outerW, outerH, cardW, cardH,
        cardX: Math.round((outerW - cardW) / 2),
        cardY: Math.round((outerH - cardH) / 2),
    };
}

/**
 * Draw the finished picture.
 *
 * @param {object} state       Everything the controls hold, see tools-screenshot.js.
 * @param {number} imageWidth  Width to render the screenshot itself at. The
 *                             canvas comes out larger by the padding and bar.
 * @returns {{canvas: HTMLCanvasElement, imageRect: {x: number, y: number, width: number, height: number}}}
 *          `imageRect` is where the screenshot ended up, which is what turns a
 *          pointer position on the preview back into a position on the image.
 */
export function compose(rawState, imageWidth) {
    const state = dressed(rawState);
    const { iw, ih, long, bar, radius, outerW, outerH, cardW, cardH, cardX, cardY } =
        layout(state, imageWidth);

    const canvas = document.createElement('canvas');
    canvas.width = outerW;
    canvas.height = outerH;
    const ctx = canvas.getContext('2d');

    const backdrop = BACKDROPS.find(b => b.id === state.backdrop) || BACKDROPS[0];
    if (backdrop.id === 'solid') {
        ctx.fillStyle = state.backdropColour;
        ctx.fillRect(0, 0, outerW, outerH);
    } else if (backdrop.id !== 'none' && backdrop.id !== 'off') {
        const gradient = ctx.createLinearGradient(0, 0, outerW, outerH);
        gradient.addColorStop(0, backdrop.from);
        gradient.addColorStop(1, backdrop.to);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, outerW, outerH);
    }

    const shadow = SHADOWS[state.shadow] || SHADOWS.none;
    if (shadow.alpha > 0) {
        // Filled opaque and then covered by the card itself. Only the part that
        // spills past the corners is ever seen.
        ctx.save();
        ctx.shadowColor = `rgba(2, 6, 23, ${shadow.alpha})`;
        ctx.shadowBlur = Math.round(shadow.blur * long);
        ctx.shadowOffsetY = Math.round(shadow.offset * long);
        ctx.fillStyle = '#000000';
        roundedPath(ctx, cardX, cardY, cardW, cardH, radius);
        ctx.fill();
        ctx.restore();
    }

    ctx.save();
    roundedPath(ctx, cardX, cardY, cardW, cardH, radius);
    ctx.clip();

    if (bar) {
        ctx.fillStyle = state.barColour;
        ctx.fillRect(cardX, cardY, cardW, bar);

        const dot = bar * 0.22;
        let dx = cardX + bar * 0.62;
        for (const colour of DOTS) {
            ctx.beginPath();
            ctx.arc(dx, cardY + bar / 2, dot, 0, Math.PI * 2);
            ctx.fillStyle = colour;
            ctx.fill();
            dx += dot * 3.2;
        }
    }

    ctx.drawImage(applyRegions(state.image, state, iw, ih), cardX, cardY + bar, iw, ih);
    ctx.restore();

    return {
        canvas,
        imageRect: { x: cardX, y: cardY + bar, width: iw, height: ih },
    };
}
